import React, { useMemo, useRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

function cn(...parts: Array<string | undefined | false | null>) {
    return parts.filter(Boolean).join(' ');
}

const tableStyles = tv({
    slots: {
        wrapper: 'relative overflow-hidden ui-card rounded-[var(--radius-lg)]',
        scroller: 'overflow-x-auto custom-scrollbar',
        table: 'min-w-full border-collapse',
        thead:
            'font-semibold uppercase tracking-[0.05em] ' +
            'border-b bg-[var(--v2-table-header-bg)]',
        trHead: '',
        th: 'text-left align-middle select-none text-[var(--v2-text-muted)] text-[0.8125rem]',
        tbody: 'font-medium',
        tr:
            'transition-colors duration-[var(--v2-duration-fast)] border-b border-[var(--v2-table-border)] last:border-b-0',
        td: 'align-middle text-[15px]',
        loader:
            'absolute inset-0 z-20 flex items-center justify-center backdrop-blur-sm bg-[var(--v2-bg-card)]/75',
        empty: 'text-center py-16',
    },
    variants: {
        size: {
            small: { th: 'px-3.5 py-2.5 text-[0.8125rem]', td: 'px-3.5 py-2.5 text-[14px]' },
            medium: { th: 'px-4.5 py-3 text-[0.8125rem]', td: 'px-4.5 py-3.5 text-[15px]' },
            large: { th: 'px-5 py-4 text-[0.875rem]', td: 'px-5 py-4 text-base' },
        },
        variant: {
            default: {},
            striped: {},
            bordered: { table: 'divide-y', wrapper: 'border-2' },
            minimal: {},
        },
        stickyHeader: { true: { thead: 'sticky top-0 z-10' } },
        compactDivider: { true: { tbody: 'divide-y' } },
        interactive: { true: {} },
        selected: { true: { tr: 'ring-1 ring-primary-200/50' } },
        stripedRow: { even: { tr: '' }, odd: {} },
    },
    compoundVariants: [{ variant: 'bordered', compactDivider: true }],
    defaultVariants: { size: 'medium', variant: 'default', stickyHeader: true, compactDivider: false },
});

type Column = {
    key: string;
    title: string;
    isAction?: boolean;
    formatter?: (value: any, row?: any, index?: number) => React.ReactNode;
    thClassName?: string;
    tdClassName?: string;
    sortable?: boolean;
    width?: string;
};

export interface TableProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof tableStyles> {
    columns: Column[];
    data?: any[] | null;
    rowKey?: (row: any, index: number) => string | number;
    emptyText?: string;
    onRowClick?: (row: any) => void;
    rowClassName?: (row: any, index: number) => string;
    loading?: boolean;
    showRowNumbers?: boolean;
    selectable?: boolean;
    selectedRows?: Set<string | number>;
    onRowSelect?: (rowKey: string | number, selected: boolean) => void;
    onSelectAll?: (selected: boolean) => void;
    theadClassName?: string;
    tbodyClassName?: string;
    trClassName?: string;
    tdClassName?: string;
}

const Table: React.FC<TableProps> = ({
    columns,
    data,
    rowKey = (_, i) => i,
    emptyText = 'No data available',
    onRowClick,
    stickyHeader = true,
    rowClassName,
    variant = 'default',
    size = 'medium',
    loading = false,
    showRowNumbers = false,
    selectable = false,
    selectedRows = new Set(),
    onRowSelect,
    onSelectAll,
    className,
    theadClassName,
    tbodyClassName,
    trClassName,
    tdClassName,
    ...rest
}) => {
    const { wrapper, scroller, table, thead, trHead, th, tbody, tr, td, loader, empty } =
        tableStyles({ size, variant, stickyHeader });

    const safeData = Array.isArray(data) ? data : [];
    const hasRows = safeData.length > 0;

    const allSelected = hasRows && safeData.every((row, idx) => selectedRows.has(rowKey(row, idx)));
    const someSelected = hasRows && safeData.some((row, idx) => selectedRows.has(rowKey(row, idx)));

    const selectAllRef = useRef<HTMLInputElement>(null);
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;

    const stripedRowVariant = useMemo<'even' | 'odd' | undefined>(
        () => (variant === 'striped' ? 'even' : undefined),
        [variant]
    );

    return (
        <div className={cn(wrapper(), className)} style={{ background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-subtle)', color: 'var(--v2-text-primary)' }} {...rest}>
            {loading && (
                <div className={loader()} style={{ background: 'var(--v2-bg-card)', opacity: 0.85 }} role="status" aria-live="polite">
                    <div className="flex items-center gap-3">
                        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                        <span className="font-medium" style={{ color: 'var(--v2-text-secondary)' }}>Loading…</span>
                    </div>
                </div>
            )}

            <div className={scroller()}>
                <table className={table()}>
                    <thead className={cn(thead(), theadClassName)} style={{ background: 'var(--v2-bg-surface)', color: 'var(--v2-text-secondary)', borderColor: 'var(--v2-border-subtle)' }}>
                        <tr className={trHead()}>
                            {selectable && (
                                <th className={cn(th(), 'w-12 text-center')}>
                                    <div className={size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3'}>
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={e => onSelectAll?.(e.target.checked)}
                                            className="h-4 w-4 rounded text-primary-600 focus:ring-2 focus:ring-primary-400"
                                            style={{ borderColor: 'var(--v2-border-subtle)' }}
                                            aria-label="Select all rows"
                                        />
                                    </div>
                                </th>
                            )}

                            {showRowNumbers && (
                                <th className={cn(th(), 'w-16 text-center')}>
                                    <div className={size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3'}>#</div>
                                </th>
                            )}

                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    style={{ width: col.width }}
                                    className={cn(th(), col.isAction ? 'text-center' : 'text-left', col.thClassName)}
                                >
                                    <div
                                        className={cn(
                                            'flex items-center gap-2',
                                            size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3',
                                            col.sortable && 'cursor-pointer rounded transition-colors'
                                        )}
                                        style={col.sortable ? { ['--tw-hover-bg' as any]: 'var(--v2-bg-hover)' } : undefined}
                                        onMouseEnter={col.sortable ? (e) => { (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-hover)'; } : undefined}
                                        onMouseLeave={col.sortable ? (e) => { (e.currentTarget as HTMLElement).style.background = ''; } : undefined}
                                    >
                                        {col.title}
                                        {col.sortable && (
                                            <svg className="h-4 w-4" style={{ color: 'var(--v2-text-muted)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                            </svg>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className={cn(tbody(), tbodyClassName)} style={{ borderColor: 'var(--v2-border-subtle)' }}>
                        {hasRows ? (
                            safeData.map((row, rowIndex) => {
                                const key = rowKey(row, rowIndex);
                                const isSelected = selectedRows.has(key);

                                const rowClasses = tableStyles({
                                    size,
                                    variant,
                                    selected: isSelected ? true : undefined,
                                    stripedRow: stripedRowVariant,
                                    interactive: onRowClick ? true : undefined,
                                });

                                return (
                                    <tr
                                        key={key}
                                        data-interactive={onRowClick ? 'true' : 'false'}
                                        className={cn(
                                            rowClasses.tr(),
                                            rowClassName?.(row, rowIndex),
                                            trClassName
                                        )}
                                        style={{
                                            background: isSelected
                                                ? 'var(--v2-bg-hover)'
                                                : variant === 'striped' && rowIndex % 2 === 0
                                                    ? 'var(--v2-table-row-even, var(--v2-bg-surface))'
                                                    : 'var(--v2-bg-card)',
                                            borderColor: 'var(--v2-border-subtle)',
                                        }}
                                        onMouseEnter={onRowClick ? (e) => { (e.currentTarget as HTMLElement).style.background = 'var(--v2-table-row-hover, var(--v2-bg-hover))'; } : undefined}
                                        onMouseLeave={onRowClick ? (e) => {
                                            (e.currentTarget as HTMLElement).style.background = isSelected
                                                ? 'var(--v2-bg-hover)'
                                                : variant === 'striped' && rowIndex % 2 === 0
                                                    ? 'var(--v2-table-row-even, var(--v2-bg-surface))'
                                                    : 'var(--v2-bg-card)';
                                        } : undefined}
                                        onClick={() => onRowClick?.(row)}
                                    >
                                        {selectable && (
                                            <td className={cn(rowClasses.td(), 'text-center', tdClassName)}>
                                                <div className={size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3'}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={e => {
                                                            e.stopPropagation();
                                                            onRowSelect?.(key, e.target.checked);
                                                        }}
                                                        className="h-4 w-4 rounded text-primary-600 focus:ring-2 focus:ring-primary-400"
                                                        style={{ borderColor: 'var(--v2-border-subtle)' }}
                                                        aria-label={`Select row ${rowIndex + 1}`}
                                                    />
                                                </div>
                                            </td>
                                        )}

                                        {showRowNumbers && (
                                            <td className={cn(rowClasses.td(), 'text-center font-mono')} style={{ color: 'var(--v2-text-muted)' }}>
                                                <div className={size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3'}>
                                                    {rowIndex + 1}
                                                </div>
                                            </td>
                                        )}

                                        {columns.map((col, colIndex) => (
                                            <td
                                                key={colIndex}
                                                className={cn(rowClasses.td(), col.isAction ? 'text-center' : 'text-left', col.tdClassName)}
                                                data-label={col.title}
                                            >
                                                <div className={size === 'large' ? 'px-6 py-4' : size === 'small' ? 'px-3 py-2' : 'px-4 py-3'}>
                                                    {col.formatter ? col.formatter(row[col.key], row, rowIndex) : (row[col.key] ?? '-')}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td className={cn(empty())} colSpan={columns.length + (selectable ? 1 : 0) + (showRowNumbers ? 1 : 0)}>
                                    <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
                                        <svg className="h-12 w-12" style={{ color: 'var(--v2-text-muted)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-base font-semibold" style={{ color: 'var(--v2-text-secondary)' }}>{emptyText}</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Table;
